import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Timer } from "lucide-react";
import { useLuminaLaunch, fmtLeft } from "@/lib/flash";
import { useLang } from "@/lib/i18n";
import { euro } from "@/lib/api";

function endLabel(ms, en) {
  return new Date(ms).toLocaleString(en ? "en-GB" : "fr-FR", { day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris" });
}

/** Bandeau du haut pendant les 72 h du lancement. */
export function LuminaBar() {
  const l = useLuminaLaunch();
  const { lang } = useLang();
  if (!l) return null;
  const en = lang === "en";
  return (
    <Link to={en ? `/en/product/${l.slug}` : `/produit/${l.slug}`} data-testid="lumina-bar" aria-label={en ? "Lumina V.G is available" : "Lumina V.G est disponible"}
      className="flex h-9 items-center justify-center gap-3 overflow-hidden whitespace-nowrap border-b border-black/20 bg-[var(--gold)] px-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)] md:text-xs">
      <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="md:hidden">Lumina V.G {euro(l.price)}</span>
      <span className="hidden md:inline">{en ? `Now available: Lumina V.G dark spot serum, ${euro(l.price)} then ${euro(l.priceAfter)}` : `Disponible : sérum anti-taches Lumina V.G à ${euro(l.price)}, puis ${euro(l.priceAfter)}`}</span>
      <span data-testid="lumina-bar-countdown" className="inline-flex items-center gap-1.5 rounded-full bg-[var(--ink)] px-3 py-0.5 font-mono tabular-nums text-[var(--gold-soft)]"><Timer className="h-3.5 w-3.5" aria-hidden="true" />{fmtLeft(l.left)}</span>
    </Link>
  );
}

/** Grande mise en avant (accueil, boutique) pendant le lancement. */
export function LuminaHero({ className }) {
  const l = useLuminaLaunch();
  const { lang } = useLang();
  if (!l) return null;
  const en = lang === "en";
  return (
    <section data-testid="lumina-hero" className={className}>
      <div className="relative mx-auto grid max-w-6xl items-center gap-6 overflow-hidden rounded-sm bg-[#141013] p-6 text-[var(--parchment)] md:grid-cols-[1.1fr_1fr] md:p-12">
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[var(--gold-soft)]">{en ? "New · available now" : "Nouveau · disponible maintenant"}</p>
          <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-6xl">Lumina V.G <span className="italic text-[var(--gold-soft)]">{en ? "is here" : "est disponible"}</span></h2>
          <p className="mt-4 max-w-xl text-base text-[var(--parchment)]/90">
            {en ? "Our dark spot serum with niacinamide, alpha-arbutin and tranexamic acid, 30 ml dropper bottle." : "Notre sérum anti-taches à la niacinamide, l'alpha-arbutine et l'acide tranexamique, en flacon pipette de 30 ml."}
          </p>
          <p data-testid="lumina-hero-price" className="mt-6 flex flex-wrap items-baseline gap-3">
            <span className="font-display text-4xl text-[var(--gold-soft)]">{euro(l.price)}</span>
            <span className="text-sm text-[var(--parchment)]/80">{en ? `until ${endLabel(l.endsAt, en)}, then ${euro(l.priceAfter)}` : `jusqu'au ${endLabel(l.endsAt, en)}, puis ${euro(l.priceAfter)}`}</span>
          </p>
          <span data-testid="lumina-hero-countdown" className="mt-3 inline-block font-mono text-2xl tabular-nums md:text-3xl">{fmtLeft(l.left)}</span>
          <div>
            <Link to={en ? `/en/product/${l.slug}` : `/produit/${l.slug}`} data-testid="lumina-hero-cta" className="group mt-7 inline-flex h-12 items-center gap-2 rounded-full bg-[var(--gold)] px-7 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] transition-transform duration-300 hover:-translate-y-0.5">
              {en ? "Discover Lumina V.G" : "Je découvre Lumina V.G"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
        <img src="/editorial/lumina-vg-email.jpg" alt={en ? "Lumina V.G dark spot serum by Ozentya" : "Le Sérum Anti-Taches Lumina V.G d'Ozentya"} className="relative w-full rounded-sm object-cover" loading="lazy" />
      </div>
    </section>
  );
}
